import { CreditCheckStatus } from '@/pages/creditCheck/creditCheckHelper';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { APIError } from '../errors/APIError';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';

export const useCreditCheckerId = () => {
  const role = useRole();
  return useAPIQuery(['getCreditCheckerId'], async client => {
    let creditCheckerId = '';
    if (role.type === RoleType.Admin) {
      const creditCheckersResponse = await client.credit.getCreditCheckers();
      if (creditCheckersResponse.status !== 200) throw APIError.fromResponse(creditCheckersResponse);
      if (creditCheckersResponse.body.items.length === 0) throw new Error('No credit checkers available');
      creditCheckerId = creditCheckersResponse.body.items[0].id;
    } else if (role.type === RoleType.CreditChecker) {
      creditCheckerId = role.creditCheckerId!;
    } else {
      throw new Error('Invalid role');
    }

    return creditCheckerId;
  });
};

export const useMspCreditChecks = () => {
  return useAPIQuery(['getMspCreditChecks'], async client => {
    const response = await client.credit.getMspCreditChecks();
    if (response.status !== 200) throw APIError.fromResponse(response);

    await Promise.all(
      response.body.items.map(async item => {
        const historyResponse = await client.credit.getMspCreditCheckHistory({ params: { mspId: item.mspId } });

        if (historyResponse.status !== 200) throw APIError.fromResponse(response);

        if (historyResponse.body?.items.length > 1) {
          // Patch up createdOn date.
          item.effectiveDate.createdOn = historyResponse.body.items.at(-1)!.effectiveDate.createdOn;
        }
      })
    );

    return response.body;
  });
};

export interface MappedCustomerCreditCheck {
  id: string;
  creditLimitId?: string;
  name: string;
  number: string;
  creditLimit?: number;
  createdAt?: Date;
  lastModified?: Date;
  userId?: string;
  status?: CreditCheckStatus;
  mspId?: string;
  regionCode?: string;
}

export const useCustomerCreditChecks = () => {
  const [creditCheckerId] = useCreditCheckerId();
  return useAPIQuery(['getCustomerCreditChecks'], async client => {
    const response = await client.credit.getCreditChecksForCreditChecker({
      params: {
        creditCheckerId: creditCheckerId!
      }
    });

    if (response.status !== 200) throw APIError.fromResponse(response);

    const creditChecks: MappedCustomerCreditCheck[] = [];

    await Promise.all(
      response.body.mspCustomerCreditChecks
        .filter(item => item.customerCreditChecks.length > 0)
        .map(async msp => {
          return await Promise.all(
            msp.customerCreditChecks.map(async item => {
              const historyResponse = await client.credit.getCustomerCreditCheckHistory({
                params: { mspId: msp.mspId, customerId: item.customerId }
              });

              if (historyResponse.status !== 200) throw APIError.fromResponse(historyResponse);

              if (historyResponse.body?.items.length > 1) {
                // Patch up createdOn date.
                item.creditCheck.effectiveDate.createdOn = historyResponse.body.items.at(-1)!.effectiveDate.createdOn;
              }

              const customerResponse = await client.credit.getCustomer({
                params: { id: item.customerId, creditCheckerId: creditCheckerId! }
              });
              if (customerResponse.status !== 200) throw APIError.fromResponse(customerResponse);

              const customer = customerResponse.body;

              creditChecks.push({
                id: item.customerId,
                creditLimitId: item.creditCheck?.id,
                name: customer.name,
                number: customer.registeredInfo.number,
                creditLimit: item.creditCheck?.creditLimit,
                createdAt: item.creditCheck?.effectiveDate.createdOn
                  ? new Date(item.creditCheck?.effectiveDate.createdOn)
                  : undefined,
                lastModified: item.creditCheck?.effectiveDate.value
                  ? new Date(item.creditCheck?.effectiveDate.value)
                  : undefined,
                userId: item.creditCheck?.effectiveDate.userId,
                status: item.creditCheck?.status,
                mspId: msp.mspId,
                regionCode: customer.region.code
              });

              return;
            })
          );
        })
    );

    // // Remove undefined entries and flatten array
    return creditChecks;
  });
};

interface CreateCreditCheckArgs {
  creditLimit: number;
}

interface MspCreateCreditCheckArgs extends CreateCreditCheckArgs {
  mspId: string;
}

export const useCreateMspCreditCheck = () => {
  return useAPIMutation((client, query) => {
    return async ({ creditLimit, mspId }: MspCreateCreditCheckArgs) => {
      const response = await client.credit.createMspCreditCheck({
        body: { creditLimit },
        params: { mspId }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getMspCreditChecks']);
      await query.invalidateQueries(['getMspCreditCheck', mspId, response.body.id]);
      await query.invalidateQueries(['getPartner', mspId]);
      return response.body;
    };
  });
};

interface CustomerCreateCreditCheckArgs extends CreateCreditCheckArgs {
  mspId: string;
  customerId: string;
  status: CreditCheckStatus;
}

export const useCreateCustomerCreditCheck = () => {
  return useAPIMutation((client, query) => {
    return async ({ creditLimit, mspId, customerId, status }: CustomerCreateCreditCheckArgs) => {
      const response = await client.credit.createCustomerCreditCheck({
        body: { creditLimit, status },
        params: { mspId, customerId }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getCustomerCreditChecks']);
      await query.invalidateQueries(['getCustomerCreditCheck', mspId, customerId, response.body.id]);
      await query.invalidateQueries(['getCompany', mspId, customerId]);
      return response.body;
    };
  });
};

export const useMspCreditCheck = (mspId: string, creditCheckId?: string) => {
  return useAPIQuery(['getMspCreditCheck', mspId, creditCheckId], async client => {
    if (!creditCheckId) return null;
    const response = await client.credit.getMspCreditCheck({ params: { mspId, id: creditCheckId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useCustomerCreditCheck = (mspId: string, customerId: string, creditCheckId?: string) => {
  return useAPIQuery(['getCustomerCreditCheck', mspId, customerId, creditCheckId], async client => {
    if (!creditCheckId) return null;
    const response = await client.credit.getCustomerCreditCheck({ params: { mspId, customerId, id: creditCheckId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useCreditCheckerCustomer = (customerId: string) => {
  const [creditCheckerId] = useCreditCheckerId();
  return useAPIQuery(['getCreditCheckerCustomer', customerId], async client => {
    const response = await client.credit.getCustomer({ params: { creditCheckerId, id: customerId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};
